'use client';

import { motion } from 'framer-motion';
import styled, { keyframes } from 'styled-components';
import { theme } from '@frameio/vapor-theme';

export const ToastActions = styled.div`
  align-items: center;
  align-self: stretch;
  display: flex;
  flex-shrink: 0;
`;

// css variables are declared in the ToastAnimation Display component
export const ToastButtonCloseContainer = styled.div`
  align-items: center;
  align-self: stretch;
  display: flex;
  flex-shrink: 0;
  margin-bottom: calc(var(--vertical-padding) * -1);
  margin-left: ${theme.spaces[8]};
  margin-top: calc(var(--vertical-padding) * -1);
`;

export const ToastAdornmentBeforeContainer = styled.div`
  align-items: center;
  align-self: stretch;
  display: flex;
`;

export const ToastDisplayMessages = styled.div`
  align-items: center;
  align-self: stretch;
  column-gap: ${theme.spaces[4]};
  display: flex;
  flex: 1;
  flex-wrap: wrap;
  margin-inline-end: ${theme.spaces[12]};
  padding-inline-start: ${theme.spaces[8]};
  row-gap: ${theme.spaces[2]};
  white-space: pre-wrap;
`;

export const ToastMotionBox = styled(motion.div)<{
  $hasHorizontalBoundaries?: boolean;
}>`
  border-radius: var(--radius);
  display: flex;
  min-height: var(--min-height);
  overflow: hidden;
  padding-block: var(--vertical-padding);
  padding-inline: var(--horizontal-padding);
  width: calc(100dvw - var(--toast-item-padding, 0));

  &[data-loading] {
    left: 0;
    min-height: var(--min-height);
    position: absolute;
    top: 0;
  }

  ${theme.mediaQueries.tablet} {
    max-width: ${(p) =>
      /* disabled for Toast Hints */ p.$hasHorizontalBoundaries === true
        ? '448px'
        : null};
    min-width: ${(p) =>
      /* disabled for Toast Hints */ p.$hasHorizontalBoundaries === true
        ? '360px'
        : null};
    width: auto;

    &[data-loading] {
      min-width: 260px;
      width: 260px;
    }
  }

  // if the toasts have icons, we need to make sure the toast message is aligned
  &:has(${ToastAdornmentBeforeContainer}) ${ToastDisplayMessages} {
    padding-inline-start: ${theme.spaces[8]};
  }

  // if the toasts have actions, we need to make sure the toast message is aligned
  &:has(${ToastActions}) ${ToastDisplayMessages} {
    margin-inline-end: ${theme.spaces[16]};
  }
`;

const toastBounceKeyframes = keyframes`
  0% {
    transform: translateX(-100%);
  }
  100% {
     transform: translateX(100%);
  }
`;

export const ToastLoadingIndicator = styled.div`
  animation-duration: 1000ms;
  animation-iteration-count: infinite;
  animation-name: ${toastBounceKeyframes};
  animation-timing-function: ease-in-out;
  background: linear-gradient(
    to right,
    transparent,
    ${theme.colors.brand.primary}
  );
  height: ${theme.spaces[2]};
  left: 0;
  position: absolute;
  right: 0;
  top: 0;
  width: 100%;
`;

/**
 * Used to display Shortcut keys in a toast message
 * examples usage:
 * <ToastShortcuts>
 *     <Shortcut>⌘</Shortcut>
 *     <Shortcut>?</Shortcut>
 * </ToastShortcuts>
 */
export const ToastShortcuts = styled.span`
  column-gap: ${theme.spaces[2]};
  display: inline-flex;
  padding-inline: ${theme.spaces[4]};
`;
